import { Stack, styled, Tooltip } from '@mui/material'
import type { UserPresentation } from 'ComponentLibrary/src/common/userPresentations'
import type { UserDisplayData } from 'ComponentLibrary/src/common/users'
import { DisplayUserLink, DisplayUserPresentation, type UserLinkCopy } from './UserLink'
import { tokens } from 'ComponentLibrary'

type UserLinkListBaseProps = {
    size?: 'small' | 'medium'
    maxWidth?: string
    copy?: Partial<UserLinkCopy>
    showAll?: boolean
    showNonUserChips?: boolean
}

type UserLinkListProps =
    | (UserLinkListBaseProps & { users: UserDisplayData[]; presentations?: never })
    | (UserLinkListBaseProps & { users?: never; presentations: UserPresentation[] })

type UserLinkListEntry =
    | { key: string; user: UserDisplayData; presentation?: never }
    | { key: string; user?: never; presentation: UserPresentation }

export function UserLinkList({ users, presentations, size, maxWidth, copy, showAll, showNonUserChips }: UserLinkListProps) {
    const entries: UserLinkListEntry[] = users
        ? users.map((user) => ({ key: `${user.first_name}-${user.last_name}-${user.organization_name}`, user }))
        : presentations.map((presentation) => ({ key: presentation.key, presentation }))

    if (entries.length === 0) {
        return null
    }
    if (showAll) {
        return (
            <Stack direction="column" gap={tokens.SizingSpacing100}>
                {entries.map((entry) => (
                    <DisplayUserListEntry
                        key={entry.key}
                        entry={entry}
                        size={size}
                        maxWidth={maxWidth}
                        copy={copy}
                        showNonUserChips={showNonUserChips}
                    />
                ))}
            </Stack>
        )
    }
    if (entries.length === 1) {
        return <DisplayUserListEntry entry={entries[0]} size={size} maxWidth={maxWidth} copy={copy} showNonUserChips={showNonUserChips} />
    }

    return (
        <Tooltip
            placement="right-end"
            slotProps={{
                popper: {
                    modifiers: [{ name: 'offset', options: { offset: [-8, -4] } }],
                },
            }}
            componentsProps={{
                tooltip: {
                    sx: {
                        ...tokens.Popup,
                        backgroundColor: tokens.ColorBackgroundLight,
                        minWidth: '232px',
                    },
                },
            }}
            title={<AssignedUsersPreview entries={entries} copy={copy} showNonUserChips={showNonUserChips} />}
        >
            <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center" width="fit-content">
                <DisplayUserListEntry
                    entry={entries[0]}
                    size={size}
                    maxWidth={maxWidth ? `calc(${maxWidth} - 26px)` : undefined}
                    showProfileOnHover={false}
                    disableEllipsisHover
                    copy={copy}
                    showNonUserChips={showNonUserChips}
                />
                <CustomerCounter $size={size}>+{entries.length - 1}</CustomerCounter>
            </Stack>
        </Tooltip>
    )
}

type DisplayUserListEntryProps = UserLinkListBaseProps & {
    entry: UserLinkListEntry
    showProfileOnHover?: boolean
    disableEllipsisHover?: boolean
}

const DisplayUserListEntry = ({ entry, showNonUserChips, ...displayProps }: DisplayUserListEntryProps) => {
    if ('user' in entry) {
        return <DisplayUserLink user={entry.user} {...displayProps} />
    }

    return <DisplayUserPresentation presentation={entry.presentation} showNonUserChip={showNonUserChips} {...displayProps} />
}

const AssignedUsersPreview = ({
    entries,
    copy,
    showNonUserChips,
}: {
    entries: UserLinkListEntry[]
    copy?: Partial<UserLinkCopy>
    showNonUserChips?: boolean
}) => {
    return (
        <Stack direction="column" gap={tokens.SizingSpacing100} padding={tokens.SizingSpacing075}>
            {entries.map((entry) => (
                <DisplayUserListEntry key={entry.key} entry={entry} copy={copy} showNonUserChips={showNonUserChips} />
            ))}
        </Stack>
    )
}

const CustomerCounter = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $size?: 'small' | 'medium' }>(({ $size = 'medium' }) => ({
    backgroundColor: $size === 'small' ? undefined : tokens.ColorSurfaceAccentGreyDefault,
    color: tokens.ColorTextDefault,
    borderRadius: tokens.SizingCornerRound,
    width: $size === 'small' ? '18px' : '28px',
    height: $size === 'small' ? '18px' : '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    ...tokens.Headline4,
}))
